/**
 * The right-side product drawer — design/PROTOTYPE-SPEC.md §3, "every row opens a
 * right-side product drawer (batches, movement history, quick logging)".
 *
 * The batch list is in FEFO order, which is the order stock will actually be
 * consumed, and the quick-log form says which lot it is about to take from
 * before you press anything. That is the point of the panel: a clinician should
 * be able to see that the system is reaching for the right vial.
 */
import {
  USAGE_PURPOSES,
  USAGE_PURPOSE_LABEL,
  WRITE_OFF_REASON_LABEL,
  amountCostPence,
  formatAmount,
} from '@inventory-tracker/core';
import { useCallback, useEffect, useState } from 'react';
import {
  discardOpenPack,
  undoDiscardOpenPack,
  getProduct,
  listClassifications,
  logUsage,
  updateBatch,
  updateProduct,
  voidMovement,
  type Classification,
  type Movement,
  type ProductDetail,
  type UsagePurpose,
} from '../lib/api.js';
import { c, field, label as labelStyle, mono, primaryButton, sans } from '../theme.js';
import { useCategories } from './CategoryField.js';
import {
  Pill,
  formatDate,
  formatDateTime,
  money,
  penceToPounds,
  poundsToPence,
  smallButton,
  unitCount,
} from './format.js';
import { ProductForm, toProductBody, type ProductFormValues } from './ProductForm.js';

interface Props {
  productId: string;
  onClose: () => void;
  /** Called after anything that changes stock, so the dashboard behind refreshes. */
  onChanged: () => void | Promise<void>;
}

const TYPE_LABEL: Record<Movement['type'], string> = {
  in: 'Received',
  out: 'Used',
  waste: 'Written off',
  adjustment: 'Adjusted',
};

export function ProductDrawer({ productId, onClose, onChanged }: Props) {
  const [data, setData] = useState<ProductDetail | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [qty, setQty] = useState('1');
  const [purpose, setPurpose] = useState<UsagePurpose>('treatment');
  const [busy, setBusy] = useState(false);
  const [flash, setFlash] = useState<string | null>(null);
  /** What a sale was charged, per unit, in pounds as typed. */
  const [price, setPrice] = useState('');
  /** Null means "oldest first", which is the default. */
  const [batchId, setBatchId] = useState<string | null>(null);
  const [choosingBatch, setChoosingBatch] = useState(false);
  const [editing, setEditing] = useState(false);
  /** The batch whose lot number and expiry are being corrected, if any. */
  const [fixing, setFixing] = useState<{ id: string; lot: string; expiry: string } | null>(null);
  const [classifications, setClassifications] = useState<Classification[]>([]);
  // Throwing away what is left in the open pack: closed, or the reason chosen.
  const [discarding, setDiscarding] = useState(false);
  const [discardReason, setDiscardReason] = useState<DiscardReason>('expired');
  const categories = useCategories(editing);

  const load = useCallback(async () => {
    try {
      setData(await getProduct(productId));
      setError(null);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not load this product');
    }
  }, [productId]);

  useEffect(() => {
    void load();
  }, [load]);

  // Escape closes, as it does on every other panel in the app.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  async function submitUsage(e: React.FormEvent) {
    e.preventDefault();
    const n = Number(qty);
    if (!Number.isInteger(n) || n < 1) {
      setError('Enter a whole number of units');
      return;
    }
    setBusy(true);
    setError(null);
    try {
      let pricePence: number | null = null;
      if (purpose === 'sold') {
        pricePence = poundsToPence(price);
        if (pricePence === null) {
          setError('Enter what it was sold for, like 31 or 31.50');
          setBusy(false);
          return;
        }
      }
      const result = await logUsage(productId, {
        qty: n,
        purpose,
        ...(purpose === 'sold' ? { pricePence } : {}),
        ...(batchId ? { batchId } : {}),
      });
      const from = result.allocations
        .map((a) => `${a.qty} from ${a.lot ?? 'unlabelled'}`)
        .join(', ');
      setFlash(`Logged ${result.qty} · ${from}`);
      setQty('1');
      setBatchId(null);
      setChoosingBatch(false);
      await load();
      await onChanged();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not log that');
    } finally {
      setBusy(false);
    }
  }

  async function beginEdit() {
    setError(null);
    setFlash(null);
    try {
      setClassifications((await listClassifications()).classifications);
      setEditing(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not open the editor');
    }
  }

  async function saveDetails(values: ProductFormValues) {
    // The form reports a refusal itself (a staff login may not edit products).
    await updateProduct(productId, { ...toProductBody(values), brand: values.brand, category: values.category });
    setEditing(false);
    setFlash('Product details saved');
    await load();
    await onChanged();
  }

  async function saveBatch() {
    if (!fixing) return;
    setBusy(true);
    setError(null);
    try {
      await updateBatch(fixing.id, {
        lot: fixing.lot.trim() === '' ? null : fixing.lot.trim(),
        expiry: fixing.expiry === '' ? null : fixing.expiry,
      });
      setFixing(null);
      setFlash('Batch corrected. The change is in the audit log.');
      await load();
      await onChanged();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not correct that batch');
    } finally {
      setBusy(false);
    }
  }

  async function throwAwayOpen() {
    setBusy(true);
    setError(null);
    try {
      const r = await discardOpenPack(productId, discardReason);
      setDiscarding(false);
      setFlash(`Thrown away, ${money(r.valuePence)} counted as waste. It is in the audit log.`);
      await load();
      await onChanged();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not throw that away');
    } finally {
      setBusy(false);
    }
  }

  async function undoThrowAway() {
    setBusy(true);
    setError(null);
    try {
      await undoDiscardOpenPack(productId);
      setFlash('Undone. The open pack is back, and it is no longer counted as waste.');
      await load();
      await onChanged();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not undo that');
    } finally {
      setBusy(false);
    }
  }

  async function undo(movementId: string) {
    setBusy(true);
    setError(null);
    try {
      await voidMovement(movementId);
      setFlash('Entry voided, stock corrected');
      await load();
      await onChanged();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not void that entry');
    } finally {
      setBusy(false);
    }
  }

  const product = data?.product;

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(20,26,22,0.38)',
        zIndex: 80,
        display: 'flex',
        justifyContent: 'flex-end',
      }}
      onClick={onClose}
    >
      <aside
        onClick={(e) => e.stopPropagation()}
        style={{
          width: 'min(520px, 100%)',
          height: '100%',
          background: c.appBg,
          borderLeft: `1px solid ${c.border}`,
          overflowY: 'auto',
          fontFamily: sans,
          color: c.ink,
        }}
      >
        <header
          style={{
            position: 'sticky',
            top: 0,
            background: c.ink,
            color: c.onDark,
            padding: '20px 24px',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'flex-start',
            gap: 12,
          }}
        >
          <div>
            <h2
              style={{
                fontFamily: sans, letterSpacing: '-0.03em',
                fontWeight: 300,
                fontSize: 26,
                margin: 0,
                color: c.onDarkBright,
              }}
            >
              {product?.name ?? 'Loading…'}
            </h2>
            {product && (
              <p style={{ margin: '6px 0 0', fontSize: 12.5, color: c.onDarkMuted }}>
                {[product.brand, product.classificationLabel].filter(Boolean).join(' · ') ||
                  'No brand'}
              </p>
            )}
          </div>
          <div style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
          {product && !editing && data?.mayEdit && (
            <button
              type="button"
              onClick={() => void beginEdit()}
              style={{
                background: 'transparent',
                border: `1px solid ${c.borderDark}`,
                color: c.onDarkSoft,
                borderRadius: 980,
                padding: '6px 12px',
                fontSize: 13,
                fontFamily: sans,
                cursor: 'pointer',
                whiteSpace: 'nowrap',
              }}
            >
              Edit product
            </button>
          )}
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            style={{
              background: 'transparent',
              border: `1px solid ${c.borderDark}`,
              color: c.onDarkSoft,
              borderRadius: 980,
              padding: '6px 12px',
              fontSize: 13,
              fontFamily: sans,
              cursor: 'pointer',
            }}
          >
            Close
          </button>
          </div>
        </header>

        <div style={{ padding: '22px 24px 60px' }}>
          {error && (
            <div
              role="alert"
              style={{
                background: c.dangerBg,
                border: `1px solid ${c.dangerBorder}`,
                borderRadius: 12,
                padding: '11px 14px',
                marginBottom: 16,
                fontSize: 13.5,
                color: c.danger,
              }}
            >
              {error}
            </div>
          )}
          {flash && (
            <div
              style={{
                background: c.okBg,
                border: `1px solid ${c.okLine}`,
                borderRadius: 12,
                padding: '11px 14px',
                marginBottom: 16,
                fontSize: 13.5,
                color: c.success,
              }}
            >
              {flash}
            </div>
          )}

          {product && (
            <>
              <div style={{ display: 'flex', gap: 8, marginBottom: 18, flexWrap: 'wrap' }}>
                <Pill tone={product.status.stock}>{product.status.stockLabel}</Pill>
                <Pill tone={product.status.expiry}>{product.status.expiryLabel}</Pill>
              </div>

              <dl
                style={{
                  display: 'grid',
                  gridTemplateColumns: '1fr 1fr',
                  gap: '14px 18px',
                  margin: '0 0 26px',
                }}
              >
                <Fact label="On hand" value={unitCount(product.qtyOnHand, product.unitLabel)} />
                <Fact label="Minimum" value={String(product.minLevel)} />
                <Fact label="Stock value" value={money(product.stockValuePence)} />
                <Fact label="Unit cost" value={money(product.unitCostPence)} />
                {/* Only for products used in parts: what a pack holds, and what
                    is left in the one already open. The open pack is not in
                    "On hand": it has already left the shelf. */}
                {product.contentUnit && (
                  <Fact
                    label="One pack holds"
                    value={`${formatAmount(product.packContentsHundredths ?? 100)} ${product.contentUnit}`}
                  />
                )}
                {product.contentUnit && (
                  <div>
                    <Fact
                      label="Left in the open pack"
                      value={
                        (product.openHundredths ?? 0) > 0
                          ? `${formatAmount(product.openHundredths ?? 0)} ${product.contentUnit}`
                          : 'None open'
                      }
                    />
                    {(product.openHundredths ?? 0) > 0 && !discarding && (
                      <button
                        type="button"
                        onClick={() => {
                          setDiscarding(true);
                          setDiscardReason('expired');
                          setFlash(null);
                        }}
                        style={linkButton(c.danger)}
                      >
                        Throw away
                      </button>
                    )}
                    {(product.openHundredths ?? 0) === 0 && data.lastDiscard && (
                      <div style={{ fontSize: 12.5, color: c.muted, marginTop: 4, lineHeight: 1.45 }}>
                        {formatAmount(data.lastDiscard.amountHundredths)} {product.contentUnit} thrown
                        away
                        {data.lastDiscard.occurredOn === data.today
                          ? ' today'
                          : ` on ${data.lastDiscard.occurredOn.split('-').reverse().join('/')}`}
                        .{' '}
                        <button
                          type="button"
                          disabled={busy}
                          onClick={() => void undoThrowAway()}
                          style={{ ...linkButton(c.green), marginTop: 0, fontSize: 12.5 }}
                        >
                          Undo
                        </button>
                      </div>
                    )}
                  </div>
                )}
              </dl>

              {/* The open pack went off or was dropped. Shelf stock is not
                  touched: the pack left the shelf when it was opened. */}
              {discarding && (product.openHundredths ?? 0) > 0 && (
                <div
                  style={{
                    border: `1px solid ${c.dangerBg}`,
                    borderRadius: 14,
                    padding: '14px 16px',
                    margin: '-12px 0 24px',
                  }}
                >
                  <div style={{ fontSize: 14.5 }}>
                    Throw away {formatAmount(product.openHundredths ?? 0)} {product.contentUnit},
                    worth{' '}
                    {money(
                      amountCostPence(
                        product.openHundredths ?? 0,
                        product.unitCostPence,
                        product.packContentsHundredths ?? 100,
                      ),
                    )}
                    ?
                  </div>
                  <div style={{ fontSize: 12.5, color: c.muted, marginTop: 3 }}>
                    It counts as waste on Analytics. Stock on the shelf does not change.
                  </div>
                  <div
                    role="radiogroup"
                    aria-label="Why it was thrown away"
                    style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 12 }}
                  >
                    {DISCARD_REASONS.map((r) => (
                      <button
                        key={r}
                        type="button"
                        role="radio"
                        aria-checked={discardReason === r}
                        onClick={() => setDiscardReason(r)}
                        style={{
                          border: 'none',
                          borderRadius: 980,
                          padding: '6px 13px',
                          fontFamily: sans,
                          fontSize: 13,
                          cursor: 'pointer',
                          background: discardReason === r ? c.dangerBg : c.well,
                          color: discardReason === r ? c.danger : c.muted,
                          fontWeight: discardReason === r ? 500 : 400,
                        }}
                      >
                        {WRITE_OFF_REASON_LABEL[r]}
                      </button>
                    ))}
                  </div>
                  <div style={{ display: 'flex', gap: 8, marginTop: 14 }}>
                    <button
                      type="button"
                      disabled={busy}
                      onClick={() => void throwAwayOpen()}
                      style={{
                        ...smallButton,
                        background: c.danger,
                        color: '#fff',
                        opacity: busy ? 0.6 : 1,
                      }}
                    >
                      {busy ? 'Throwing away…' : 'Throw away'}
                    </button>
                    <button type="button" onClick={() => setDiscarding(false)} style={smallButton}>
                      Cancel
                    </button>
                  </div>
                </div>
              )}

              {editing && (
                <Section title="Edit product">
                  <ProductForm
                    initial={{
                      name: product.name,
                      brand: product.brand,
                      category: product.category,
                      classificationId: product.classificationId,
                      unitCostPence: product.unitCostPence,
                      retailPricePence: product.retailPricePence,
                      minLevel: product.minLevel,
                      packContentsHundredths: product.packContentsHundredths ?? 100,
                      contentUnit: product.contentUnit ?? null,
                    }}
                    classifications={classifications}
                    categories={categories}
                    submitLabel="Save changes"
                    onSubmit={saveDetails}
                    onCancel={() => setEditing(false)}
                  />
                </Section>
              )}

              <Section title="Log use">
                <form onSubmit={submitUsage} style={{ display: 'grid', gap: 12 }}>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1.4fr', gap: 12 }}>
                    <div>
                      <label style={labelStyle} htmlFor="drawer-qty">
                        Quantity
                      </label>
                      <input
                        id="drawer-qty"
                        value={qty}
                        onChange={(e) => setQty(e.target.value)}
                        inputMode="numeric"
                        style={field}
                      />
                    </div>
                    <div>
                      <label style={labelStyle} htmlFor="drawer-purpose">
                        Purpose
                      </label>
                      <select
                        id="drawer-purpose"
                        value={purpose}
                        onChange={(e) => {
                          const next = e.target.value as UsagePurpose;
                          setPurpose(next);
                          // Start from the offer when one is running, otherwise
                          // the retail price; a discount is typed over it.
                          if (next === 'sold' && price === '') {
                            setPrice(
                              penceToPounds(data.promotion?.pricePence ?? product.retailPricePence),
                            );
                          }
                        }}
                        style={{ ...field, padding: '0 10px' }}
                      >
                        {USAGE_PURPOSES.map((k) => (
                          <option key={k} value={k}>
                            {USAGE_PURPOSE_LABEL[k]}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>

                  {purpose === 'sold' && (
                    <div>
                      <label style={labelStyle} htmlFor="drawer-price">
                        Sold for, each (£)
                      </label>
                      <input
                        id="drawer-price"
                        value={price}
                        onChange={(e) => setPrice(e.target.value)}
                        inputMode="decimal"
                        placeholder="0.00"
                        style={field}
                      />
                      {data.promotion && (
                        <OfferNote
                          promotion={data.promotion}
                          fullPricePence={product.retailPricePence}
                          atOffer={poundsToPence(price) === data.promotion.pricePence}
                          onPrice={(pence) => setPrice(penceToPounds(pence))}
                        />
                      )}
                    </div>
                  )}

                  {/* Oldest first unless told otherwise. The override is here
                      for when the vial in your hand is not the one the app
                      would pick: recording the wrong lot is worse than using
                      stock out of order. */}
                  {data.batches[0] && !choosingBatch && (
                    <p style={{ fontSize: 13, color: c.muted, margin: 0 }}>
                      Will take from <strong>{data.batches[0].lot ?? 'unlabelled batch'}</strong>{' '}
                      first, expires {formatDate(data.batches[0].expiry)}.
                      {data.batches.length > 1 && (
                        <>
                          {' '}
                          <button
                            type="button"
                            onClick={() => {
                              setChoosingBatch(true);
                              setBatchId(data.batches[0]!.id);
                            }}
                            style={{
                              border: 'none',
                              background: 'none',
                              padding: 0,
                              fontFamily: sans,
                              fontSize: 13,
                              color: c.green,
                              cursor: 'pointer',
                              textDecoration: 'underline',
                              textUnderlineOffset: 3,
                            }}
                          >
                            Use a different batch
                          </button>
                        </>
                      )}
                    </p>
                  )}

                  {choosingBatch && (
                    <div>
                      <label style={labelStyle} htmlFor="drawer-batch">
                        Take from
                      </label>
                      <select
                        id="drawer-batch"
                        value={batchId ?? ''}
                        onChange={(e) => setBatchId(e.target.value)}
                        style={{ ...field, padding: '0 10px' }}
                      >
                        {data.batches.map((b, i) => (
                          <option key={b.id} value={b.id}>
                            {b.lot ?? 'No lot number'} · {b.qtyRemaining} left ·{' '}
                            {b.expiry ? `expires ${formatDate(b.expiry)}` : 'no expiry'}
                            {i === 0 ? ' (oldest)' : ''}
                          </option>
                        ))}
                      </select>
                      {batchId !== null && batchId !== data.batches[0]?.id && (
                        <p
                          style={{
                            background: c.amberBg,
                            border: `1px solid ${c.amberLine}`,
                            color: c.amberText,
                            borderRadius: 12,
                            padding: '9px 12px',
                            fontSize: 13,
                            margin: '8px 0 0',
                          }}
                        >
                          {data.batches[0]?.lot ?? 'Another batch'} expires sooner. Using this one
                          instead will be noted in the audit log.
                        </p>
                      )}
                      <button
                        type="button"
                        onClick={() => {
                          setChoosingBatch(false);
                          setBatchId(null);
                        }}
                        style={{
                          border: 'none',
                          background: 'none',
                          padding: '8px 0 0',
                          fontFamily: sans,
                          fontSize: 13,
                          color: c.green,
                          cursor: 'pointer',
                          textDecoration: 'underline',
                          textUnderlineOffset: 3,
                        }}
                      >
                        Go back to oldest first
                      </button>
                    </div>
                  )}

                  <button
                    type="submit"
                    disabled={busy || product.qtyOnHand === 0}
                    style={{
                      ...primaryButton,
                      height: 46,
                      opacity: busy || product.qtyOnHand === 0 ? 0.55 : 1,
                      cursor: product.qtyOnHand === 0 ? 'not-allowed' : 'pointer',
                    }}
                  >
                    {product.qtyOnHand === 0 ? 'Nothing in stock' : busy ? 'Logging…' : 'Log use'}
                  </button>
                </form>
              </Section>

              <Section title={`Batches (${data.batches.length})`}>
                {data.batches.length === 0 ? (
                  <Empty>No stock on the shelf.</Empty>
                ) : (
                  <ul style={{ listStyle: 'none', margin: 0, padding: 0 }}>
                    {data.batches.map((b, i) => (
                      <li
                        key={b.id}
                        style={{
                          display: 'flex',
                          flexWrap: 'wrap',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          gap: 12,
                          padding: '11px 0',
                          borderTop: i === 0 ? 'none' : `1px solid ${c.border}`,
                        }}
                      >
                        <div>
                          <div style={{ fontSize: 13.5, fontWeight: 500 }}>
                            {b.lot ?? 'No lot number'}
                            {i === 0 && (
                              <span
                                style={{ fontSize: 11, color: c.green, marginLeft: 8 }}
                                title="First-expiry-first-out"
                              >
                                next out
                              </span>
                            )}
                          </div>
                          <div style={{ fontSize: 12, color: c.muted, marginTop: 2 }}>
                            {b.qtyRemaining} of {b.qtyReceived} left · {money(b.valuePence)}
                            {b.locationLabel ? ` · ${b.locationLabel}` : ''}
                          </div>
                        </div>
                        <div style={{ textAlign: 'right' }}>
                          <Pill tone={b.expiryStatus}>{b.expiryLabel}</Pill>
                          <div style={{ fontSize: 11.5, color: c.faint, marginTop: 4 }}>
                            {formatDate(b.expiry)}
                          </div>
                          {data.mayEdit && fixing?.id !== b.id && (
                            <button
                              type="button"
                              onClick={() =>
                                setFixing({ id: b.id, lot: b.lot ?? '', expiry: b.expiry ?? '' })
                              }
                              style={{
                                border: 'none',
                                background: 'none',
                                padding: '5px 0 0',
                                fontFamily: sans,
                                fontSize: 12.5,
                                color: c.green,
                                cursor: 'pointer',
                                textDecoration: 'underline',
                                textUnderlineOffset: 3,
                              }}
                            >
                              Correct lot or expiry
                            </button>
                          )}
                        </div>
                        {/* Fixing a typo made when the delivery was booked in.
                            Quantity is not here on purpose: a wrong quantity is
                            corrected with Void or a stock count, so the record
                            of what happened stays whole. */}
                        {fixing?.id === b.id && (
                          <div
                            style={{
                              flexBasis: '100%',
                              display: 'grid',
                              gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
                              gap: 10,
                              marginTop: 4,
                            }}
                          >
                            <div>
                              <label style={labelStyle} htmlFor="fix-lot">
                                Lot number
                              </label>
                              <input
                                id="fix-lot"
                                value={fixing.lot}
                                onChange={(e) => setFixing({ ...fixing, lot: e.target.value })}
                                style={{ ...field, height: 44, fontSize: 15 }}
                              />
                            </div>
                            <div>
                              <label style={labelStyle} htmlFor="fix-expiry">
                                Expiry date
                              </label>
                              <input
                                id="fix-expiry"
                                type="date"
                                value={fixing.expiry}
                                onChange={(e) => setFixing({ ...fixing, expiry: e.target.value })}
                                style={{ ...field, height: 44, fontSize: 15 }}
                              />
                            </div>
                            <div style={{ gridColumn: '1 / -1', display: 'flex', gap: 8 }}>
                              <button
                                type="button"
                                disabled={busy}
                                onClick={() => void saveBatch()}
                                style={{
                                  ...smallButton,
                                  background: c.green,
                                  color: c.white,
                                  height: 38,
                                  padding: '0 16px',
                                }}
                              >
                                {busy ? 'Saving…' : 'Save correction'}
                              </button>
                              <button
                                type="button"
                                onClick={() => setFixing(null)}
                                style={{ ...smallButton, height: 38, padding: '0 16px' }}
                              >
                                Cancel
                              </button>
                            </div>
                          </div>
                        )}
                      </li>
                    ))}
                  </ul>
                )}
              </Section>

              <Section title="Recent activity">
                {data.movements.length === 0 ? (
                  <Empty>Nothing has moved yet.</Empty>
                ) : (
                  <ul style={{ listStyle: 'none', margin: 0, padding: 0 }}>
                    {data.movements.map((m, i) => (
                      <li
                        key={m.id}
                        style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          gap: 12,
                          padding: '11px 0',
                          borderTop: i === 0 ? 'none' : `1px solid ${c.border}`,
                          opacity: m.isVoided ? 0.5 : 1,
                        }}
                      >
                        <div>
                          <div
                            style={{
                              fontSize: 13.5,
                              fontWeight: 500,
                              textDecoration: m.isVoided ? 'line-through' : 'none',
                            }}
                          >
                            {TYPE_LABEL[m.type]} {m.qty}
                            {m.lot ? ` · ${m.lot}` : ''}
                          </div>
                          <div style={{ fontSize: 12, color: c.muted, marginTop: 2 }}>
                            {formatDateTime(m.createdAt)}
                            {m.byName ? ` · ${m.byName}` : ''}
                            {m.reason ? ` · ${m.reason}` : ''}
                          </div>
                        </div>
                        {/* A reversal is itself a movement and cannot be voided. */}
                        {!m.isVoided && !m.voidsMovementId && (
                          <button
                            type="button"
                            disabled={busy}
                            onClick={() => undo(m.id)}
                            style={smallButton}
                          >
                            Void
                          </button>
                        )}
                      </li>
                    ))}
                  </ul>
                )}
              </Section>
            </>
          )}
        </div>
      </aside>
    </div>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section
      style={{
        background: c.card,
        border: `1px solid ${c.border}`,
        borderRadius: 18,
        padding: '18px 20px',
        marginBottom: 16,
      }}
    >
      <h3 style={{ fontFamily: sans, letterSpacing: '-0.03em', fontWeight: 400, fontSize: 19, margin: '0 0 12px' }}>
        {title}
      </h3>
      {children}
    </section>
  );
}

/**
 * The offer running on this product. A sale counts toward the promotion only
 * when it is charged the offer price (the server decides), so the note says
 * which way this sale will go and offers the other with one tap.
 */
function OfferNote({
  promotion,
  fullPricePence,
  atOffer,
  onPrice,
}: {
  promotion: NonNullable<ProductDetail['promotion']>;
  fullPricePence: number | null;
  atOffer: boolean;
  onPrice: (pence: number) => void;
}) {
  const until = promotion.endsOn ? ` until ${formatDate(promotion.endsOn)}` : '';
  return (
    <div
      style={{
        background: atOffer ? c.okBg : c.well,
        color: atOffer ? c.okText : c.muted,
        borderRadius: 10,
        padding: '8px 11px',
        fontSize: 13,
        lineHeight: 1.45,
        marginTop: 8,
      }}
    >
      {atOffer ? (
        <>
          {promotion.offer}
          {until}: {money(promotion.pricePence)}
          {fullPricePence !== null && <> instead of {money(fullPricePence)}</>}. Counts toward the
          promotion.{' '}
          {fullPricePence !== null && (
            <button
              type="button"
              onClick={() => onPrice(fullPricePence)}
              style={{ ...linkButton(c.okText), marginTop: 0 }}
            >
              Sell at full price
            </button>
          )}
        </>
      ) : (
        <>
          {promotion.offer} is running{until}. This price is not the offer, so the sale will not
          count toward it.{' '}
          <button
            type="button"
            onClick={() => onPrice(promotion.pricePence)}
            style={{ ...linkButton(c.green), marginTop: 0 }}
          >
            Use the offer, {money(promotion.pricePence)}
          </button>
        </>
      )}
    </div>
  );
}

type DiscardReason = 'expired' | 'damaged' | 'contaminated' | 'other';
const DISCARD_REASONS: DiscardReason[] = ['expired', 'damaged', 'contaminated', 'other'];

const linkButton = (color: string): React.CSSProperties => ({
  border: 'none',
  background: 'none',
  padding: 0,
  marginTop: 4,
  fontFamily: sans,
  fontSize: 13,
  color,
  cursor: 'pointer',
  textDecoration: 'underline',
  textUnderlineOffset: 3,
});

function Fact({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <dt
        style={{
          fontSize: 11,
          textTransform: 'lowercase', fontFamily: mono,
          color: c.label,
          marginBottom: 4,
        }}
      >
        {label}
      </dt>
      <dd style={{ margin: 0, fontSize: 15, fontWeight: 500 }}>{value}</dd>
    </div>
  );
}

function Empty({ children }: { children: React.ReactNode }) {
  return <p style={{ fontSize: 13, color: c.muted, margin: 0 }}>{children}</p>;
}
